/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  signal
} from '@angular/core';
import {NgForm} from '@angular/forms';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {firstValueFrom, Subscription} from 'rxjs';
import * as _ from 'lodash';

import template from './creative-preview-modal.component.html';
import {isPoliticalPreviewCreative} from './ad/preview-language';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LivePreviewApiService} from './live-preview-api.service';
import {CreativeApiService} from './creative-api.service';

@Component({
  standalone: false,
  selector: 'adn-creative-preview-modal',
  template
})
export class CreativePreviewModalComponent implements OnInit, OnDestroy {
  private _livePreviewForm: NgForm | null = null;

  @ViewChild('livePreviewForm')
  set livePreviewForm(form: NgForm | null) {
    if (form === this._livePreviewForm) {
      return;
    }
    this._livePreviewForm = form;
    this.formStatusSub?.unsubscribe();
    this.formStatusSub = null;
    if (form) {
      this.scheduleFormBind();
    }
  }

  get livePreviewForm(): NgForm | null {
    return this._livePreviewForm;
  }

  creative: any;
  lineItem: any;
  teamId: string;

  showLivePreviewTab = false;
  htmlUseCreativeId = false;
  advertiser: any;
  election: any;
  layoutId: string;
  displayCreativeDanger = false;
  displayLineItemDanger = false;
  viewUrl: string;
  adnPreviewLink: string;
  livePreviewLink: string;

  readonly isLoadingCreative = signal(false);
  readonly livePreviewProcessing = signal(false);
  /**
   * Template [disabled] — start true and only flip on a macrotask after NgForm
   * settle. Reading form.invalid in a getter during the first CD is NG0100
   * (ViewChild missing → true, then form appears valid/invalid → false).
   */
  readonly createDisabled = signal(true);

  private formStatusSub: Subscription | null = null;
  private formBindTimer: ReturnType<typeof setTimeout> | null = null;
  private disabledSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private assetsPromise: any;
  private destroyed = false;

  constructor(
    public bsModalRef: BsModalRef,
    private readonly livePreviewApi: LivePreviewApiService,
    private readonly creativeApi: CreativeApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any
  ) {
  }

  get previewContext(): this {
    return this;
  }

  ngOnInit(): void {
    this.showLivePreviewTab = !this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.advertiser = _.get(this.lineItem, 'advertiser');
    this.election = _.get(this.lineItem, 'election');
    this.layoutId = _.get(this.creative, ['layout', 'id']);
    this.creative.isPoliticalPreview = isPoliticalPreviewCreative(this.creative) ||
      isPoliticalPreviewCreative({lineItem: this.lineItem});

    // Line-item list entries are summaries. Only choose the ID-preview path
    // after the full creative has been loaded.
    if (!this.creative.layoutParameters || _.isEmpty(this.creative.layoutParameters)) {
      this.isLoadingCreative.set(true);
      firstValueFrom(this.creativeApi.getHydratedPreview(this.creative.id)).then((c: any) => {
        if (this.destroyed) {
          return;
        }
        this.creative = c;
        this.layoutId = _.get(c, ['layout', 'id']) || this.layoutId;
        this.creative.isPoliticalPreview = isPoliticalPreviewCreative(c) ||
          isPoliticalPreviewCreative({lineItem: this.lineItem});
        this.updateHtmlUseCreativeId(this.creative);
        this.isLoadingCreative.set(false);
      }, () => {
        if (this.destroyed) {
          return;
        }
        this.isLoadingCreative.set(false);
      });
    }

    this.displayCreativeDanger = !!_.find(this.creative.validationWarnings, ['preventsAdServing', true]);

    const badState = this.lineItem.userState !== 'RESERVED' &&
      this.lineItem.userState !== 'APPROVED' &&
      this.lineItem.userState !== 'PAUSED';
    const badLineItem = _.filter(this.lineItem.validationWarnings, (w: any) => {
      return w.preventsAdServing && w.code !== 'validation.warning.lineItem.unapproved';
    }).length > 0;
    this.displayLineItemDanger = badState || badLineItem || this.lineItem.executionState === 'ENDED';
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.formBindTimer !== null) {
      clearTimeout(this.formBindTimer);
      this.formBindTimer = null;
    }
    if (this.disabledSyncTimer !== null) {
      clearTimeout(this.disabledSyncTimer);
      this.disabledSyncTimer = null;
    }
    this.formStatusSub?.unsubscribe();
    this.formStatusSub = null;
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  createLivePreview(): void {
    const delimiter = _.indexOf(this.viewUrl, '?') > 10 ? '&' : '?';
    this.adnPreviewLink = `${this.viewUrl}${delimiter}adn-lp-li=${this.lineItem.id}&adn-lp-c=${this.creative.id}`;

    this.livePreviewProcessing.set(true);
    this.createDisabled.set(true);
    this.assetsPromise = _.get(this.creative, ['assets'])
      ? Promise.resolve(this.creative)
      : (this.assetsPromise || firstValueFrom(this.creativeApi.hydrate(this.creative)));
    this.assetsPromise.then((c: any) => {
      const layoutParams: any = {};
      _.forEach(c.layoutParameters, (v: any, k: string) => {
        if (c.layoutParameters[k] && c.layoutParameters[k].id) {
          const assetId = c.layoutParameters[k].id;
          layoutParams[k] = _.pick((_.find(c.assets, ['id', assetId]) || {}), ['cdnId', 'previewCdnId', 'width', 'height', 'content']);
        } else {
          layoutParams[k] = v;
        }
      });

      this.livePreviewApi.create(
        this.viewUrl,
        this.LocalNetworkProfile.getNetworkTag(),
        this.teamId,
        _.get(c, ['layout', 'id']),
        layoutParams,
        this.creative.width,
        this.creative.height
      ).subscribe({
        next: (data) => {
          this.livePreviewProcessing.set(false);
          this.livePreviewLink = data.livePreviewLink;
          this.scheduleCreateDisabledSync();
        },
        error: () => {
          this.livePreviewProcessing.set(false);
          this.scheduleCreateDisabledSync();
        }
      });
    }, () => {
      this.livePreviewProcessing.set(false);
      this.scheduleCreateDisabledSync();
    });
  }

  private updateHtmlUseCreativeId(creative: any): void {
    this.htmlUseCreativeId = !!_.find(_.get(creative, ['layoutParameters']), (v: any) => v.content);
    const creativeInIndex = !_.find(_.get(creative, ['validationWarnings']), (v: any) => {
      return v.preventsAdServing || v.code === 'validation.warning.creative.line.item.ended';
    });
    this.htmlUseCreativeId = this.htmlUseCreativeId && creativeInIndex;
  }

  private scheduleFormBind(): void {
    if (!this.showLivePreviewTab || this.destroyed) {
      return;
    }
    if (this.formBindTimer !== null) {
      clearTimeout(this.formBindTimer);
    }
    this.formBindTimer = setTimeout(() => {
      this.formBindTimer = null;
      if (this.destroyed || !this.showLivePreviewTab) {
        return;
      }
      const form = this._livePreviewForm;
      if (!form) {
        return;
      }
      if (!this.formStatusSub) {
        this.formStatusSub = form.statusChanges?.subscribe(() => {
          this.scheduleCreateDisabledSync();
        }) || null;
      }
      this.syncCreateDisabled();
    }, 0);
  }

  private scheduleCreateDisabledSync(): void {
    if (this.disabledSyncTimer !== null) {
      clearTimeout(this.disabledSyncTimer);
    }
    this.disabledSyncTimer = setTimeout(() => {
      this.disabledSyncTimer = null;
      if (this.destroyed) {
        return;
      }
      this.syncCreateDisabled();
    }, 0);
  }

  private syncCreateDisabled(): void {
    const form = this.livePreviewForm;
    const next = this.livePreviewProcessing() || !form || !!form.invalid || !!form.pending;
    this.createDisabled.set(next);
  }
}
